import {
  QueryClient,
  keepPreviousData as placeholderData,
  useQuery,
} from '@tanstack/react-query';
import {
  APIQueryInput,
  APIQueryOptions,
  APIQueryResult,
} from 'shared/api/APIQuery';
import { APISchema, castAPIData } from 'shared/api/APISchema';
import { APIPageResponse } from 'shared/api/CarrierAPIClient';
import { THIRTY_SECONDS } from 'shared/constants/NumberConstants';

export interface APIPageQueryOptions<TData> extends APIQueryOptions<
  APIPageResponse<TData>
> {
  schema?: APISchema<TData>;
  normalize?: (response: unknown) => TData;
  keepPreviousData?: boolean;
}

export type APIPageQueryResult<TData> = APIQueryResult<APIPageResponse<TData>>;

export function useAPIPageQuery<TData>(
  queryKey: APIQueryInput,
  query: () => Promise<APIPageResponse<TData>>,
  {
    schema,
    normalize,
    keepPreviousData = true,
    staleTime = THIRTY_SECONDS,
    ...options
  }: APIPageQueryOptions<TData> = {},
): APIPageQueryResult<TData> {
  return useQuery({
    queryKey,
    queryFn: () =>
      query().then((response) => {
        if (schema || normalize) {
          response.data = response.data.map((data) => {
            if (schema) {
              data = castAPIData(schema, data);
            }

            if (normalize) {
              data = normalize(data);
            }

            return data;
          });
        }

        return response;
      }),
    ...options,
    staleTime,
    placeholderData: keepPreviousData ? placeholderData : undefined,
  });
}

export function findAPIPageQueryItem<TData>(
  input: APIQueryInput,
  queryClient: QueryClient,
  fn: (item: TData) => boolean,
): undefined | TData {
  for (const { state } of queryClient
    .getQueryCache()
    .findAll({ queryKey: input })) {
    if (state.data) {
      const { data } = state.data as APIPageResponse<TData>;

      for (const item of data) {
        if (fn(item)) {
          return item;
        }
      }
    }
  }

  return undefined;
}
